import React, { useState } from "react";
import {
  MdAddChart,
  MdCheck,
  MdDashboard,
  MdGroups,
  MdLock,
  MdOutlineNotifications,
  MdPalette,
  MdSettingsInputAntenna,
} from "react-icons/md";
import { AiOutlineFieldTime } from "react-icons/ai";
import { useParams } from "react-router-dom";
import { useAppSelector } from "../../../../app/hooks";
import { selectGroupById } from "../../../../features/groups/groupSlice";
import { PricePeriod, isStyleFeatureAvailable } from "../utils";
import { OnPlanSelectType } from "./AvailablePlans";
import useGetLanguage from "../../../../utils/useGetLanguage";
import { LanguageText } from "../../../../dictionary/GroupsText";
import PerkDisplay from "./PerkDisplay";

export interface AvailablePlan {
  id: string;
  name: string;
  perks: {
    devices: number;
    users: number;
    dashboards: number;
    dashboard_elements: number;
    notification_alerts: number;
    data_retention: {
      days: number;
      months: number;
      years: number;
    };
  };
  price: {
    monthly: {
      amount: number; // in pence
      currency: string;
      stripe_price_id: string;
      xero_catalog_id: string;
    };
    annually: {
      amount: number; // in pence
      currency: string;
      stripe_price_id: string;
      xero_catalog_id: string;
    };
  };
  active: boolean;
  extra_features: any[];
  sale: {
    active: boolean;
    discount_percentage: number;
  };
}

interface PriceDisplayProps {
  price: AvailablePlan["price"];
  sale: AvailablePlan["sale"];
  selectedPeriod: PricePeriod;
  onPeriodChange: (period: PricePeriod) => void;
}

const PriceDisplay: React.FC<PriceDisplayProps> = ({
  price,
  sale,
  selectedPeriod,
  onPeriodChange,
}) => {
  const language: LanguageText = useGetLanguage({
    fileName: "GroupsText",
  });

  const calcPriceAfterDiscount = (rawPrice: number) => {
    if (!sale.active) return rawPrice;
    return rawPrice - (rawPrice * sale.discount_percentage) / 100;
  };

  const monthlyPricePence = calcPriceAfterDiscount(price.monthly.amount);
  const annualPricePence = calcPriceAfterDiscount(price.annually.amount);

  // Convert pence to pounds with correct formatting:
  const formatPricePounds = (pence: number) => {
    // If exact amount in pounds (multiple of 100), display without decimals
    if (pence % 100 === 0) return (pence / 100).toString();
    // Otherwise show two decimals
    return (pence / 100).toFixed(2);
  };

  const displayPrice =
    selectedPeriod === PricePeriod.MONTHLY
      ? formatPricePounds(monthlyPricePence)
      : formatPricePounds(annualPricePence);

  return (
    <div className="bg-gray-50 border rounded-md p-3 sm:p-4 shadow flex flex-col items-center text-center w-full min-w-0 min-h-[120px]">
      <div className="flex justify-center items-center mb-3 sm:mb-4 w-full">
        <div className="flex rounded-md border border-gray-300 overflow-hidden text-xs sm:text-sm select-none w-full max-w-xs">
          <button
            className={`px-2 py-1.5 sm:px-3 sm:py-2 font-semibold transition-colors duration-200 flex-1 text-center ${
              selectedPeriod === PricePeriod.MONTHLY
                ? "bg-blue-600 text-white"
                : "bg-white text-gray-700 hover:bg-gray-100"
            }`}
            onClick={() => onPeriodChange(PricePeriod.MONTHLY)}
            type="button"
          >
            {language?.groupSubscription?.monthly}
          </button>
          <button
            className={`px-2 py-1.5 sm:px-3 sm:py-2 font-semibold transition-colors duration-200 flex-1 text-center ${
              selectedPeriod === PricePeriod.YEARLY
                ? "bg-blue-600 text-white"
                : "bg-white text-gray-700 hover:bg-gray-100"
            }`}
            onClick={() => onPeriodChange(PricePeriod.YEARLY)}
            type="button"
          >
            {language?.groupSubscription?.yearly}
          </button>
        </div>
      </div>

      <div className="text-blue-600 font-semibold text-2xl md:text-3xl h-16 flex items-center justify-center">
        <span className="text-center">
          £{displayPrice}
          <span className="ml-1 text-gray-600 font-normal text-xs md:text-sm">
            /{" "}
            {selectedPeriod === PricePeriod.MONTHLY
              ? language?.groupSubscription?.month
              : language?.groupSubscription?.year}
          </span>
        </span>
      </div>
      {sale.active && (
        <div className="mt-1 text-xs md:text-sm text-green-600 font-semibold whitespace-nowrap">
          {sale.discount_percentage}% - {language?.groupSubscription?.off}
        </div>
      )}
    </div>
  );
};

interface AvailablePlanCardProps {
  plan: AvailablePlan;
  onPlanSelect: (selectedPlan: OnPlanSelectType) => void;
}

const AvailablePlanCard: React.FC<AvailablePlanCardProps> = ({ plan, onPlanSelect }) => {
  const language: LanguageText = useGetLanguage({
    fileName: "GroupsText",
  });
  const { groupid } = useParams<{ groupid: string }>();
  const groupInfo = useAppSelector((state) => selectGroupById(state, groupid ?? ""));
  const { id: planID, name, perks, price, sale } = plan;
  const [selectedPeriod, setSelectedPeriod] = useState<PricePeriod>(PricePeriod.MONTHLY);

  const calcPriceAfterDiscount = (rawPrice: number) => {
    if (!sale.active) return rawPrice;
    return rawPrice - (rawPrice * sale.discount_percentage) / 100;
  };

  const selectedPrice =
    selectedPeriod === PricePeriod.MONTHLY
      ? calcPriceAfterDiscount(price.monthly.amount)
      : calcPriceAfterDiscount(price.annually.amount);

  const selectedPriceID = price[selectedPeriod].stripe_price_id;

  const formatDataRetention = (d: number, m: number, y: number) => {
    const parts = [];
    if (d)
      parts.push(
        `${d} ${
          d > 1 ? language?.groupSubscription?.days : language?.groupSubscription?.day
        }`
      );
    if (m)
      parts.push(
        `${m} ${
          m > 1 ? language?.groupSubscription?.months : language?.groupSubscription?.month
        }`
      );
    if (y)
      parts.push(
        `${y} ${
          y > 1 ? language?.groupSubscription?.years : language?.groupSubscription?.year
        }`
      );
    return parts.join(" ");
  };

  const handlePeriodChange = (period: PricePeriod) => setSelectedPeriod(period);

  const handlePlanSelect = () => {
    const selectedPlan: OnPlanSelectType = {
      period: selectedPeriod,
      price: selectedPrice / 100,
      priceID: selectedPriceID,
      groupID: groupid ?? "",
      currentPlanName: groupInfo?.subscription?.plan_name ?? "",
      selectedPlanName: name,
    };
    onPlanSelect(selectedPlan);
  };

  return (
    <div className="w-full p-3 sm:p-4 md:p-6 bg-gray-200 border border-gray-200 rounded-lg shadow space-y-3 sm:space-y-4 md:space-y-6 overflow-hidden flex flex-col min-h-[500px]">
      <div
        className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-center font-bold text-gray-900 mb-1 break-words w-full"
        style={{ minWidth: 0 }}
      >
        {name}
      </div>

      <PriceDisplay
        price={price}
        sale={sale}
        selectedPeriod={selectedPeriod}
        onPeriodChange={handlePeriodChange}
      />

      <div className="flex flex-col gap-1.5 sm:gap-2 w-full min-w-0">
        <PerkDisplay
          icon={<MdGroups size={28} />}
          value={perks.users}
          label={language?.groupSubscription?.members}
        />
        <PerkDisplay
          icon={<MdDashboard size={28} />}
          value={perks.dashboards}
          label={language?.groupSubscription?.dashboards}
        />
        <PerkDisplay
          icon={<MdSettingsInputAntenna size={28} />}
          value={perks.devices}
          label={language?.groupSubscription?.devices}
        />
        <PerkDisplay
          icon={<AiOutlineFieldTime size={28} />}
          value={formatDataRetention(
            perks.data_retention.days,
            perks.data_retention.months,
            perks.data_retention.years
          )}
          label={language?.groupSubscription?.dataRetention}
        />
        <PerkDisplay
          icon={<MdAddChart size={28} />}
          value={
            perks.dashboard_elements === -1
              ? language?.groupSubscription?.unlimited
              : perks.dashboard_elements
          }
          label={language?.groupSubscription?.dashboardElements}
        />
        <PerkDisplay
          icon={<MdOutlineNotifications size={28} />}
          value={perks.notification_alerts}
          label={language?.groupSubscription?.notificationAlerts}
        />
        <PerkDisplay
          icon={<MdPalette size={28} />}
          value={language?.groupSubscription?.included ?? "Included"}
          label={language?.groupSubscription?.customStyleAndUseAtLogin ?? "Custom style & use style at login"}
          crossedOut={!isStyleFeatureAvailable(name)}
          availabilityIcon={
            isStyleFeatureAvailable(name) ? (
              <MdCheck size={18} />
            ) : (
              <MdLock size={18} />
            )
          }
          availabilityLabel={
            isStyleFeatureAvailable(name)
              ? undefined
              : (language?.groupSubscription?.availableOnTier3 ?? "Available on Tier 3")
          }
        />
      </div>

      <div className="mt-auto">
        {groupInfo?.owner && groupInfo?.subscription?.plan_id !== planID && (
          <button
            className="w-full bg-blue-600 text-white py-3 rounded-md font-semibold hover:bg-blue-700 transition-colors duration-300"
            onClick={handlePlanSelect}
          >
            {language?.groupSubscription?.selectPlan}
          </button>
        )}
      </div>
    </div>
  );
};

export default AvailablePlanCard;
